import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Ticket, Power } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const inputCls =
  "w-full rounded-xl border border-white/10 bg-[#0A0B0E] px-4 py-2.5 text-sm text-slate-200 placeholder:text-slate-600 focus:border-yellow-300/50 focus:outline-none";

const blank = { code: "", percent: 5, min_total: 15000, expires_on: "", active: true };

/** Discount codes customers type at checkout, e.g. 5% off on orders of 15,000+. */
export function DiscountCodes() {
  const [codes, setCodes] = useState([]);
  const [form, setForm] = useState(blank);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [welcome, setWelcome] = useState(null);

  const loadWelcome = useCallback(() => {
    api.get("/admin/welcome-code").then(({ data }) => setWelcome(data)).catch(() => {});
  }, []);

  const saveWelcome = async (patch) => {
    const next = { ...welcome, ...patch };
    setBusy(true);
    try {
      const { data } = await api.put("/admin/welcome-code", {
        active: next.active,
        percent: Number(next.percent) || 0,
        min_total: Number(next.min_total) || 0,
        days: Number(next.days) || 30,
      });
      setWelcome({ ...data, issued: welcome.issued, used: welcome.used });
      toast.success(next.active ? "Welcome code is on for new customers" : "Welcome code switched off");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const load = useCallback(() => {
    api.get("/admin/coupons").then(({ data }) => setCodes(data)).catch(() => setCodes([]));
  }, []);

  useEffect(load, [load]);
  useEffect(loadWelcome, [loadWelcome]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        percent: Number(form.percent),
        min_total: Number(form.min_total) || 0,
        expires_on: form.expires_on || "",
        active: form.active,
      };
      if (editing) await api.put(`/admin/coupons/${editing}`, payload);
      else await api.post("/admin/coupons", payload);
      toast.success(editing ? "Discount code updated" : "Discount code created");
      setForm(blank);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const turnOff = async (id) => {
    try {
      await api.delete(`/admin/coupons/${id}`);
      toast.success("Code switched off");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-[#12141A] p-5" data-testid="discount-codes-box">
      <div className="flex items-center gap-2">
        <Ticket size={18} className="text-yellow-300" />
        <p className="font-display text-lg font-bold">Discount codes</p>
      </div>
      <p className="mt-1 text-xs text-slate-500">
        Customers type the code at checkout. It only works when the order reaches the minimum you set here.
      </p>

      <div className="mt-4 grid gap-3 md:grid-cols-5">
        <div className="md:col-span-2">
          <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Code</label>
          <input data-testid="coupon-code-field" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="VARSHA15K" className={`${inputCls} mt-1 font-mono uppercase`} />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">% off</label>
          <input data-testid="coupon-percent-field" type="number" min="1" max="90" value={form.percent} onChange={(e) => setForm({ ...form, percent: e.target.value })} className={`${inputCls} mt-1`} />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Min order ₹</label>
          <input data-testid="coupon-min-field" type="number" min="0" value={form.min_total} onChange={(e) => setForm({ ...form, min_total: e.target.value })} className={`${inputCls} mt-1`} />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Expires on</label>
          <input data-testid="coupon-expiry-field" type="date" value={form.expires_on} onChange={(e) => setForm({ ...form, expires_on: e.target.value })} className={`${inputCls} mt-1`} />
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          data-testid="coupon-save-btn"
          onClick={save}
          disabled={busy || !form.code.trim()}
          className="rounded-full bg-gradient-to-r from-yellow-300 to-amber-500 px-6 py-2.5 text-sm font-bold text-[#0A0B0E] disabled:opacity-40"
        >
          {busy ? "Saving…" : editing ? "Update code" : "Create code"}
        </button>
        {editing && (
          <button data-testid="coupon-cancel-btn" onClick={() => { setEditing(null); setForm(blank); }} className="text-xs text-slate-400 hover:text-slate-200">
            Cancel
          </button>
        )}
      </div>

      <div className="mt-6 rounded-xl border border-white/10 bg-black/30 p-4" data-testid="welcome-code-box">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-bold text-slate-100">Welcome code for new customers</p>
            <p className="mt-0.5 text-[11px] text-slate-500">
              Every customer who registers gets their own one-time code (WEL…). It works once, only for their number.
              {welcome ? ` ${welcome.issued} issued · ${welcome.used} used` : ""}
            </p>
          </div>
          <button
            data-testid="welcome-code-toggle"
            onClick={() => saveWelcome({ active: !welcome.active })}
            className={`rounded-full border px-4 py-2 text-xs font-bold ${welcome?.active ? "border-green-400/40 bg-green-400/10 text-green-300" : "border-white/10 text-slate-400"}`}
          >
            {welcome?.active ? "On" : "Off"}
          </button>
        </div>
        {welcome?.active && (
          <div className="mt-3 grid gap-3 md:grid-cols-4">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">% off</label>
              <input data-testid="welcome-percent-field" type="number" min="1" max="90" value={welcome.percent} onChange={(e) => setWelcome({ ...welcome, percent: e.target.value })} className={`${inputCls} mt-1`} />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Min order ₹</label>
              <input data-testid="welcome-min-field" type="number" min="0" value={welcome.min_total} onChange={(e) => setWelcome({ ...welcome, min_total: e.target.value })} className={`${inputCls} mt-1`} />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Valid for (days)</label>
              <input data-testid="welcome-days-field" type="number" min="1" max="365" value={welcome.days} onChange={(e) => setWelcome({ ...welcome, days: e.target.value })} className={`${inputCls} mt-1`} />
            </div>
            <div className="flex items-end">
              <button
                data-testid="welcome-save-btn"
                onClick={() => saveWelcome({})}
                disabled={busy}
                className="h-11 w-full rounded-full border border-yellow-300/40 bg-yellow-300/10 text-xs font-bold uppercase tracking-[0.15em] text-yellow-300 disabled:opacity-40"
              >
                Save rules
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="mt-5 space-y-2" data-testid="coupon-list">        {codes.length === 0 && <p className="text-xs text-slate-500">No discount codes yet.</p>}
        {codes.map((c) => (
          <div key={c.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/10 bg-black/30 px-4 py-3" data-testid={`coupon-row-${c.code}`}>
            <div>
              <p className="font-mono text-sm font-bold tracking-[0.15em] text-yellow-300">{c.code}</p>
              <p className="mt-0.5 text-[11px] text-slate-500">
                {c.percent}% off on orders of ₹{Number(c.min_total).toLocaleString("en-IN")}+
                {c.expires_on ? ` · till ${c.expires_on}` : " · no expiry"}
                {` · used ${c.used_count} time${c.used_count === 1 ? "" : "s"}`}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`font-mono text-[10px] uppercase tracking-[0.2em] ${c.active ? "text-green-400" : "text-slate-600"}`} data-testid={`coupon-status-${c.code}`}>
                {c.active ? "live" : "off"}
              </span>
              <button
                data-testid={`coupon-edit-${c.code}`}
                onClick={() => { setEditing(c.id); setForm({ code: c.code, percent: c.percent, min_total: c.min_total, expires_on: c.expires_on || "", active: c.active }); }}
                className="rounded-full border border-white/10 px-3 py-1.5 text-xs text-slate-300 hover:bg-white/5"
              >
                Edit
              </button>
              {c.active && (
                <button
                  data-testid={`coupon-off-${c.code}`}
                  onClick={() => turnOff(c.id)}
                  className="flex items-center gap-1 rounded-full border border-red-400/30 px-3 py-1.5 text-xs text-red-300 hover:bg-red-400/10"
                >
                  <Power size={12} /> Off
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
